import {
  APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT,
  APPLICATION_COMPOSITION_HANDOFF_CONTRACT,
  buildGridoneReportProductApplicationFixture,
  buildGridoneValidationHarnessFixture,
  validateProductHarnessSeparation,
  type ApplicationCompositionFullClosureProof,
} from '@cogo/headless-core/application-composition-full-closure'
import type { AppSpecFull } from '@/services/block-api-service'
import fullClosureProofJson from '@/data/application-composition-full-closure-proof.json' with {
  type: 'json',
}
import gridoneReportContractJson from '../../../../../../packages/cogo-validator/ontology/gridone-backoffice-report-contract-v1.json' with {
  type: 'json',
}

export const STUDIO_REPORT_APPLICATION_INTEGRATION_SCHEMA_VERSION = 'studio-report-application-integration.v1'

type ReadinessStatus = 'ready' | 'gap'

export interface StudioReportApplicationIntegrationEvidence {
  schemaVersion: typeof STUDIO_REPORT_APPLICATION_INTEGRATION_SCHEMA_VERSION
  status: ReadinessStatus
  source: string
  generatedAt: string
  contractVersion: string
  applicationKind: 'cogo.report-application'
  reportApplicationSchemaVersion: string
  reportApplicationKind: string
  productArtifactId: string
  validationHarnessArtifactId: string
  productHarnessBoundary: {
    status: string
    productAudience: string
    harnessAudience: string
    validationHarnessSeparated: boolean
    failures: string[]
  }
  fullClosureProof: {
    schemaVersion: string
    status: string
    workstreamCount: number
    browserE2eProofCommand: string
    sourceFirstProofCommand: string
    requiredGateIds: string[]
  }
  validatorContract: {
    schemaVersion: string
    status: string
    publicDomain: string
    requiredProofGates: string[]
  }
  localDatasourcePolicy: {
    acceptedRuntimeSources: string[]
    excelRuntimeDependency: boolean
    excelUse: string
    rawWorkbookFormulaAllowed: boolean
  }
  designSystemInjection: {
    designSystemId: string
    uiOwns: string
    designSystemOwns: string
    rawVisualClassNamesAllowedInCogoUi: boolean
  }
  readiness: {
    status: ReadinessStatus
    publishBlocked: boolean
    requiredEvidence: string[]
    evidenceRefs: string[]
    reasons: string[]
  }
  handoff: {
    schemaVersion: string
    methodologyDoc: string
    closureDoc: string
    proofArtifact: string
    studioConsumes: string[]
    studioMustNotDo: string[]
  }
}

export interface StudioReportExportReadinessEvidence {
  schemaVersion: 'studio-report-export-readiness-contract.v1'
  contractVersion: string
  status: ReadinessStatus
  required: true
  evidenceRefs: string[]
  artifacts: string[]
  sources: string[]
  reasons: string[]
  readinessPacket: {
    schemaVersion: 'studio-report-application-readiness-packet.v1'
    status: ReadinessStatus
    publishAllowed: boolean
    blockingFailures: string[]
    evidenceRefs: string[]
    policy: {
      localDatasourceOnly: boolean
      excelRuntimeDependency: boolean
      productHarnessSeparated: boolean
      cogoXyzOnly: boolean
    }
  }
}

interface IntegrationOptions {
  prompt?: string
  source?: string
  generatedAt?: string
}

const FULL_CLOSURE_PROOF = fullClosureProofJson as unknown as ApplicationCompositionFullClosureProof &
  Record<string, unknown>
const GRIDONE_REPORT_CONTRACT = gridoneReportContractJson as Record<string, unknown>

const REPORT_APPLICATION_BLOCKS: AppSpecFull['blocks'] = [
  {
    name: 'global-command-header',
    type: 'command-header',
    description:
      'Global command header for the report application with WebMCP chat command input and report-wide period/frequency controls.',
    binds: [
      'chat command submit -> loadReport ActionFlow -> appData.reportSource + appData.reportRows + _results.loadReportResult',
      'period filter change -> selectTable ActionFlow -> local.activeFilter + appData.filteredRows + computed.reportSummary',
    ],
  },
  {
    name: 'report-navigation',
    type: 'sidebar-navigation',
    description:
      'Collapsible full-height left navigation for report sections; navigation state must stay local to the report shell.',
    binds: ['sidebar section tap -> selectSection ActionFlow -> local.selectedSection + _results.selectSectionResult'],
  },
  {
    name: 'a4-report-canvas',
    type: 'document-page',
    description:
      'A4 portrait report canvas with visible narrative, mixed bar/line chart, and source-backed data table; use semantic @ds tokens only.',
    binds: [
      'report canvas init -> loadReport ActionFlow -> appData.previewChartRows + appData.previewTableRows + computed.reportNarrative',
    ],
  },
  {
    name: 'report-chart-table-composition',
    type: 'chart-table-composition',
    description:
      'Source-backed narrative, mixed bar/line chart surface, and report data table linked to the same local CSV/json manifest datasource.',
    binds: [
      'chart series toggle tap -> selectChart ActionFlow -> local.activeChart + computed.reportNarrative + _results.selectChartResult',
    ],
  },
  {
    name: 'document-export-panel',
    type: 'document-export-panel',
    description:
      'Document export panel for PDF, DOCX/HWPX, XLSX, and CSV export readiness using local generated artifacts, not Excel runtime formulas.',
    binds: ['export document tap -> exportDocument ActionFlow -> appData.exportArtifacts + _results.exportResult'],
  },
]

const REPORT_APPLICATION_DATASOURCES: NonNullable<AppSpecFull['datasources']> = [
  {
    name: 'localCsvManifest',
    type: 'local-file',
    schema: {
      fileName: 'string',
      sheetName: 'string',
      rowCount: 'number',
      checksum: 'string',
    },
  },
  {
    name: 'reportChartSurfaces',
    type: 'json-manifest',
    schema: {
      chartId: 'string',
      chartKind: 'string',
      sourceRange: 'string',
      rendered: 'boolean',
    },
  },
  {
    name: 'documentExportArtifacts',
    type: 'local-artifact',
    schema: {
      format: 'string',
      artifactRef: 'string',
      readiness: 'string',
      generatedAt: 'string',
    },
  },
]

const REPORT_APPLICATION_GLOBAL_STATE: NonNullable<AppSpecFull['globalState']> = {
  appData: {
    type: 'object',
    default: {
      reportSource: null,
      reportRows: [],
      filteredRows: [],
      previewChartRows: [],
      previewTableRows: [],
      exportArtifacts: [],
    },
  },
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    : []
}

function uniqueStrings(values: readonly string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort()
}

export function isReportApplicationPrompt(prompt: string): boolean {
  return (
    /\b(report|reporting|document|pdf|docx|hwp|hwpx|excel|xlsx|spreadsheet|workbook|worksheet|source[-\s]?first\s+report|document\s+export|report\s+export)\b/i.test(
      prompt,
    ) || /(보고서|리포트|엑셀|문서|내보내기)/i.test(prompt)
  )
}

function shouldAttachReportApplicationIntegration(appSpec: AppSpecFull, prompt?: string): boolean {
  const explicitKind = [
    appSpec.applicationKind,
    appSpec.reportApplicationSchemaVersion,
    appSpec.reportApplicationKind,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  const intent = prompt?.trim() || appSpec.description || ''
  return (
    appSpec.applicationKind === 'cogo.report-application' ||
    explicitKind.includes('cogo.report-application') ||
    explicitKind.includes('report-application') ||
    isReportApplicationPrompt(intent)
  )
}

function mergeBlocks(blocks: AppSpecFull['blocks']): AppSpecFull['blocks'] {
  const byName = new Set(blocks.map((block) => block.name))
  return [...blocks, ...REPORT_APPLICATION_BLOCKS.filter((block) => !byName.has(block.name))]
}

function mergePages(pages: AppSpecFull['pages'], blocks: AppSpecFull['blocks']): AppSpecFull['pages'] {
  const requiredBlockNames = REPORT_APPLICATION_BLOCKS.map((block) => block.name)
  if (!pages || pages.length === 0) {
    return [
      {
        id: 'report',
        title: 'Report',
        route: '/',
        blocks: requiredBlockNames,
      },
    ]
  }

  return pages.map((page, index) => {
    if (index !== 0) return page
    const existing = new Set(page.blocks ?? [])
    return {
      ...page,
      blocks: [
        ...(page.blocks ?? blocks.map((block) => block.name)),
        ...requiredBlockNames.filter((name) => !existing.has(name)),
      ],
    }
  })
}

function mergeDatasources(datasources: AppSpecFull['datasources']): NonNullable<AppSpecFull['datasources']> {
  const existing = datasources ?? []
  const byName = new Set(existing.map((datasource) => datasource.name))
  return [...existing, ...REPORT_APPLICATION_DATASOURCES.filter((datasource) => !byName.has(datasource.name))]
}

function mergeGlobalState(globalState: AppSpecFull['globalState']): AppSpecFull['globalState'] {
  const existingAppData = readRecord(globalState?.appData?.default)
  return {
    ...(globalState ?? {}),
    appData: {
      type: 'object',
      default: {
        ...readRecord(REPORT_APPLICATION_GLOBAL_STATE.appData.default),
        ...existingAppData,
      },
    },
  }
}

function mergeInteractionContract(
  interactionContract: AppSpecFull['interactionContract'],
): AppSpecFull['interactionContract'] {
  const base = interactionContract ?? {
    actionFlowRequired: true,
    requiredEventTypes: [],
    requiredStateScopes: [],
    requireResultsTrace: true,
    requireComputedProjection: true,
    notes: [],
  }
  return {
    ...base,
    actionFlowRequired: true,
    requiredEventTypes: uniqueStrings([...base.requiredEventTypes, 'change', 'tap', 'submit', 'fileSelect']),
    requiredStateScopes: uniqueStrings([...base.requiredStateScopes, 'local', 'appData', '_results', 'computed']),
    requireResultsTrace: true,
    requireComputedProjection: true,
    notes: uniqueStrings([
      ...base.notes,
      'Report applications must load local file/CSV/json-manifest sources, render a user-facing A4 report shell, and expose document export readiness.',
      'Excel is extraction-only; generated apps must not depend on workbook formulas at runtime.',
      'Validation harness artifacts stay separate from the user-facing cogo.report-application product shell.',
    ]),
  }
}

function buildEvidenceRefs(contractVersion: string): string[] {
  const productContract = readRecord(GRIDONE_REPORT_CONTRACT.productApplicationContract)
  const browserContract = readRecord(GRIDONE_REPORT_CONTRACT.productReportBrowserProofContract)
  return uniqueStrings([
    APPLICATION_COMPOSITION_HANDOFF_CONTRACT.proofArtifact,
    APPLICATION_COMPOSITION_HANDOFF_CONTRACT.methodologyDoc,
    APPLICATION_COMPOSITION_HANDOFF_CONTRACT.closureDoc,
    APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.sourceFirstProofCommand,
    APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.productBrowserProofCommand,
    readString(productContract.artifact),
    readString(productContract.proofArtifact),
    readString(browserContract.artifact),
    readString(browserContract.hydratedArtifact),
    `packages/cogo-validator/ontology/gridone-backoffice-report-contract-v1.json#${contractVersion}`,
  ])
}

export function buildStudioReportApplicationIntegrationEvidence(
  options: IntegrationOptions = {},
): StudioReportApplicationIntegrationEvidence {
  const product = buildGridoneReportProductApplicationFixture()
  const harness = buildGridoneValidationHarnessFixture()
  const separation = validateProductHarnessSeparation({ product, harness })
  const productContract = readRecord(GRIDONE_REPORT_CONTRACT.productApplicationContract)
  const sourceRuntimePolicy = readRecord(GRIDONE_REPORT_CONTRACT.sourceRuntimePolicy)
  const designSystemInjection = readRecord(productContract.designSystemInjection)
  const contractVersion = readString(GRIDONE_REPORT_CONTRACT.schemaVersion, 'cogo.backoffice-report-contract.v1')
  const fullClosureStatus = readString(FULL_CLOSURE_PROOF.status)
  const contractStatus = readString(GRIDONE_REPORT_CONTRACT.status)
  const ready =
    fullClosureStatus === 'pass' &&
    separation.status === 'pass' &&
    (contractStatus === 'promoted' || contractStatus === 'pass')
  const evidenceRefs = buildEvidenceRefs(contractVersion)
  const reasons = [
    ...(fullClosureStatus === 'pass'
      ? []
      : [`Application composition full-closure proof is ${fullClosureStatus || 'missing'}.`]),
    ...(separation.status === 'pass' ? [] : separation.failures),
    ...(contractStatus === 'promoted' || contractStatus === 'pass'
      ? []
      : [`Gridone report validator contract is ${contractStatus || 'missing'}.`]),
  ]
  const status: ReadinessStatus = ready ? 'ready' : 'gap'

  return {
    schemaVersion: STUDIO_REPORT_APPLICATION_INTEGRATION_SCHEMA_VERSION,
    status,
    source: options.source ?? 'studio-report-application-integration',
    generatedAt: options.generatedAt ?? new Date().toISOString(),
    contractVersion,
    applicationKind: 'cogo.report-application',
    reportApplicationSchemaVersion: readString(
      productContract.reportApplicationSchemaVersion,
      'cogo.report-application.v1',
    ),
    reportApplicationKind: readString(productContract.reportApplicationKind, 'source-first-backoffice-report'),
    productArtifactId: product.artifactId,
    validationHarnessArtifactId: harness.artifactId,
    productHarnessBoundary: {
      status: separation.status,
      productAudience: product.targetAudience,
      harnessAudience: harness.targetAudience,
      validationHarnessSeparated: separation.productArtifactId !== separation.harnessArtifactId,
      failures: [...separation.failures],
    },
    fullClosureProof: {
      schemaVersion: readString(FULL_CLOSURE_PROOF.schemaVersion),
      status: fullClosureStatus,
      workstreamCount: Array.isArray(FULL_CLOSURE_PROOF.workstreams) ? FULL_CLOSURE_PROOF.workstreams.length : 0,
      browserE2eProofCommand: APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.productBrowserProofCommand,
      sourceFirstProofCommand: APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.sourceFirstProofCommand,
      requiredGateIds: [...APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.requiredGateIds],
    },
    validatorContract: {
      schemaVersion: contractVersion,
      status: contractStatus,
      publicDomain: readString(GRIDONE_REPORT_CONTRACT.publicDomain, 'cogo.xyz'),
      requiredProofGates: readStringList(GRIDONE_REPORT_CONTRACT.requiredProofGates),
    },
    localDatasourcePolicy: {
      acceptedRuntimeSources: readStringList(sourceRuntimePolicy.acceptedRuntimeSources),
      excelRuntimeDependency: readBoolean(sourceRuntimePolicy.excelRuntimeDependency),
      excelUse: readString(sourceRuntimePolicy.excelUse, 'extraction-only'),
      rawWorkbookFormulaAllowed: readBoolean(sourceRuntimePolicy.rawWorkbookFormulaAllowed),
    },
    designSystemInjection: {
      designSystemId: readString(designSystemInjection.designSystemId, 'reb-rone-report-v1'),
      uiOwns: readString(designSystemInjection.uiOwns, 'structure-semantics-data-actionflow'),
      designSystemOwns: readString(designSystemInjection.designSystemOwns, 'visual-surface-color-radius-typography'),
      rawVisualClassNamesAllowedInCogoUi: readBoolean(designSystemInjection.rawVisualClassNamesAllowedInCogoUi),
    },
    readiness: {
      status,
      publishBlocked: !ready,
      requiredEvidence: [
        'application-composition-full-closure-proof',
        'gridone-report-validator-contract',
        'product-harness-separation',
        'source-first-browser-proof',
        'document-export-parity',
      ],
      evidenceRefs,
      reasons,
    },
    handoff: {
      schemaVersion: APPLICATION_COMPOSITION_HANDOFF_CONTRACT.schemaVersion,
      methodologyDoc: APPLICATION_COMPOSITION_HANDOFF_CONTRACT.methodologyDoc,
      closureDoc: APPLICATION_COMPOSITION_HANDOFF_CONTRACT.closureDoc,
      proofArtifact: APPLICATION_COMPOSITION_HANDOFF_CONTRACT.proofArtifact,
      studioConsumes: [...APPLICATION_COMPOSITION_HANDOFF_CONTRACT.studioConsumes],
      studioMustNotDo: [...APPLICATION_COMPOSITION_HANDOFF_CONTRACT.studioMustNotDo],
    },
  }
}

export function buildStudioReportExportReadinessEvidence(
  integration: StudioReportApplicationIntegrationEvidence,
): StudioReportExportReadinessEvidence {
  return {
    schemaVersion: 'studio-report-export-readiness-contract.v1',
    contractVersion: integration.contractVersion,
    status: integration.status,
    required: true,
    evidenceRefs: integration.readiness.evidenceRefs,
    artifacts: integration.readiness.evidenceRefs.filter(
      (ref) => ref.endsWith('.json') || ref.endsWith('.cogo-v2.json') || ref.includes('proof'),
    ),
    sources: [
      integration.fullClosureProof.sourceFirstProofCommand,
      integration.fullClosureProof.browserE2eProofCommand,
      integration.handoff.proofArtifact,
    ],
    reasons: integration.readiness.reasons,
    readinessPacket: {
      schemaVersion: 'studio-report-application-readiness-packet.v1',
      status: integration.status,
      publishAllowed: integration.status === 'ready',
      blockingFailures: integration.readiness.reasons,
      evidenceRefs: integration.readiness.evidenceRefs,
      policy: {
        localDatasourceOnly: integration.localDatasourcePolicy.acceptedRuntimeSources.every((source) =>
          ['local-file', 'csv', 'json-manifest'].includes(source),
        ),
        excelRuntimeDependency: integration.localDatasourcePolicy.excelRuntimeDependency,
        productHarnessSeparated: integration.productHarnessBoundary.validationHarnessSeparated,
        cogoXyzOnly: integration.validatorContract.publicDomain === 'cogo.xyz',
      },
    },
  }
}

function buildReportDatasourceQuality(
  datasources: NonNullable<AppSpecFull['datasources']>,
  integration: StudioReportApplicationIntegrationEvidence,
): NonNullable<AppSpecFull['generationQuality']>['datasources'] {
  return {
    required: true,
    items: datasources.map((datasource) => ({
      datasourceName: datasource.name,
      status: 'seeded',
      responseKind: 'json',
      sampleRowCount: Math.max(1, readNumber(readRecord(datasource.schema).sampleRowCount, 1)),
      evidenceRef: integration.handoff.proofArtifact,
      source: 'studio-report-application-integration',
      notes: [
        `Runtime source policy: ${integration.localDatasourcePolicy.acceptedRuntimeSources.join(', ')}`,
        'Excel workbook formulas are extraction-only and are not runtime dependencies.',
      ],
    })),
  }
}

export function withStudioReportApplicationIntegration<T extends AppSpecFull>(
  appSpec: T,
  options: IntegrationOptions = {},
): T {
  if (!shouldAttachReportApplicationIntegration(appSpec, options.prompt)) return appSpec

  const integration = buildStudioReportApplicationIntegrationEvidence(options)
  const reportExportReadiness = buildStudioReportExportReadinessEvidence(integration)
  const blocks = mergeBlocks(appSpec.blocks)
  const datasources = mergeDatasources(appSpec.datasources)
  const pages = mergePages(appSpec.pages, blocks)

  return {
    ...appSpec,
    applicationKind: 'cogo.report-application',
    targetAudience: 'user-facing',
    isValidationHarness: false,
    reportApplicationSchemaVersion: integration.reportApplicationSchemaVersion,
    reportApplicationKind: integration.reportApplicationKind,
    blocks,
    pages,
    datasources,
    globalState: mergeGlobalState(appSpec.globalState),
    interactionContract: mergeInteractionContract(appSpec.interactionContract),
    generationQuality: {
      ...(appSpec.generationQuality ?? {}),
      datasources: buildReportDatasourceQuality(datasources, integration),
    },
    reportApplicationIntegration: integration,
    reportExportReadiness,
    reportReadiness: reportExportReadiness,
    documentExportReadiness: reportExportReadiness,
  }
}
